import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { fmtDate } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { RequestForm } from "@/components/site/RequestForm";

function PageHead({ title, sub }) {
  return (
    <>
      <p className="eyebrow">GoldenTime</p>
      <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl text-[#F3EFE6]">{title}</h1>
      <p className="mt-5 text-[#A39E93] max-w-lg leading-relaxed">{sub}</p>
    </>
  );
}

export function Orders() {
  const { t, r } = useT();
  const { openAdvisor, settings } = useSite();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tipo") === "especifico" ? "specific" : "order";
  const [watches, setWatches] = useState([]);
  useEffect(() => { api.get("/public/watches").then((x) => setWatches(x.data.filter((w) => w.availability !== "reserved"))); }, []);
  const preselect = watches.find((w) => w.slug === sp.get("relogio"));
  return (
    <div className="max-w-[900px] mx-auto px-5 sm:px-10 pt-16" data-testid="orders-page">
      <PageHead title={t.orders_title} sub={t.orders_sub} />
      <div className="mt-14 flex gap-8 border-b border-[#282520]" role="tablist">
        {[["order", t.tab_catalog, ""], ["specific", t.tab_specific, "especifico"]].map(([k, l, v]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setSp(v ? { tipo: v } : {}, { replace: true })}
            className={`pb-4 text-xs uppercase tracking-[0.25em] border-b transition-colors duration-300 -mb-px ${tab === k ? "border-[#C8AE6E] text-[#E6D5AC]" : "border-transparent text-[#A39E93] hover:text-[#F3EFE6]"}`} data-testid={`orders-tab-${k}`}>{l}</button>
        ))}
      </div>
      <div className="mt-12">
        {tab === "specific" ? (
          <>
            <p className="mb-10 font-display italic text-lg text-[#F3EFE6]/75">{t.concierge_sub}</p>
            <RequestForm key="specific" type="specific" source="encomendas" testid="specific-request-form" />
          </>
        ) : (
          <RequestForm key={`order-${preselect?.id || ""}`} type="catalog" source="encomendas" watches={watches} prefill={{ watch_id: preselect?.id }} testid="order-request-form" />
        )}
      </div>
      <div className="mt-16 border-t border-[#282520] pt-8 flex flex-wrap items-center justify-between gap-4">
        {settings?.ai?.enabled && <button onClick={() => openAdvisor()} className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#C8AE6E] hover:text-[#E6D5AC]" data-testid="orders-advisor-button"><Sparkles className="w-4 h-4" strokeWidth={1.3} /> {t.orders_ai}</button>}
        <Link to={r.track} className="text-xs uppercase tracking-[0.22em] text-[#A39E93] hover:text-[#E6D5AC]" data-testid="orders-track-link">{t.nav_track} →</Link>
      </div>
    </div>
  );
}

export function Contact() {
  const { t } = useT();
  return (
    <div className="max-w-[900px] mx-auto px-5 sm:px-10 pt-16" data-testid="contact-page">
      <PageHead title={t.contact_title} sub={t.contact_sub} />
      <div className="mt-14"><RequestForm type="contact" testid="contact-form" /></div>
    </div>
  );
}

const LEGAL = {
  privacy: {
    pt: ["Política de Privacidade", [
      ["Responsável pelo tratamento", "[A COMPLETAR: denominação social], NIPC [A COMPLETAR], com sede em [A COMPLETAR: morada]. Contacto: WhatsApp +351 916 230 822."],
      ["Dados recolhidos", "Nome, telefone / WhatsApp, forma de tratamento e conteúdo dos pedidos submetidos nos formulários do site ou do consultor. Não recolhemos dados de pagamento."],
      ["Finalidade e fundamento", "Responder a pedidos de contacto, encomendas e pedidos específicos, com base no seu consentimento e em diligências pré-contratuais."],
      ["Consultor de inteligência artificial", "O consultor é um sistema de IA. As conversas são guardadas para melhoria do serviço e acompanhamento comercial. Pode pedir o apagamento dos seus dados a qualquer momento, incluindo na própria conversa."],
      ["Conservação", "[PROVISÓRIO] Pedidos sem seguimento: 90 dias. Pedidos com relação comercial: 24 meses."],
      ["Direitos", "Acesso, retificação, apagamento, limitação, oposição e portabilidade, por WhatsApp (+351 916 230 822). Pode apresentar reclamação à CNPD (www.cnpd.pt)."],
    ]],
    en: ["Privacy Policy", [
      ["Controller", "[TO COMPLETE: company name], VAT [TO COMPLETE], registered at [TO COMPLETE: address]. Contact: WhatsApp +351 916 230 822."],
      ["Data collected", "Name, phone / WhatsApp, form of address and the content of requests submitted through the website forms or the advisor. We do not collect payment data."],
      ["Purpose and legal basis", "To answer contact requests, orders and specific requests, based on your consent and pre-contractual steps."],
      ["AI advisor", "The advisor is an AI system. Conversations are stored for service improvement and sales follow-up. You may request deletion of your data at any time, including within the conversation."],
      ["Retention", "[DRAFT] Requests without follow-up: 90 days. Requests with a commercial relationship: 24 months."],
      ["Your rights", "Access, rectification, erasure, restriction, objection and portability via WhatsApp (+351 916 230 822). You may lodge a complaint with the CNPD (www.cnpd.pt)."],
    ]],
  },
  terms: {
    pt: ["Termos e Condições", [
      ["Identificação", "[A COMPLETAR: denominação social], NIPC [A COMPLETAR], morada [A COMPLETAR]."],
      ["Natureza do site", "Este site apresenta relógios novos e autênticos. Não é possível comprar nem pagar online: cada venda é confirmada diretamente com a equipa."],
      ["Preços e disponibilidade", "Preços em euros com IVA incluído [A CONFIRMAR]. Disponibilidade e preço final são sempre confirmados pela equipa antes da venda."],
      ["Consultor de IA", "As respostas do consultor são informativas e não constituem proposta contratual."],
      ["Resolução de litígios", "[A COMPLETAR: entidade de resolução alternativa de litígios]."],
    ]],
    en: ["Terms & Conditions", [
      ["Identification", "[TO COMPLETE: company name], VAT [TO COMPLETE], address [TO COMPLETE]."],
      ["Nature of the site", "This site presents new, authentic watches. Online purchase and payment are not available: every sale is confirmed directly with the team."],
      ["Prices and availability", "Prices in euros including VAT [TO CONFIRM]. Availability and final price are always confirmed by the team before sale."],
      ["AI advisor", "The advisor's answers are informative and do not constitute a contractual offer."],
      ["Dispute resolution", "[TO COMPLETE: alternative dispute resolution entity]."],
    ]],
  },
};

export function Legal({ kind }) {
  const { lang } = useT();
  const [title, sections] = LEGAL[kind][lang];
  return (
    <div className="max-w-[760px] mx-auto px-5 sm:px-10 pt-16" data-testid={`legal-${kind}-page`}>
      <p className="eyebrow">Legal</p>
      <h1 className="mt-4 text-4xl sm:text-5xl text-[#F3EFE6]">{title}</h1>
      <p className="mt-6 text-xs text-[#e0b97f] border border-[#e0b97f]/30 p-3">{lang === "pt" ? "Texto provisório — a rever e completar antes da publicação." : "Draft text — to be reviewed and completed before publication."}</p>
      <div className="mt-12 space-y-10">
        {sections.map(([h, p]) => <section key={h}><h2 className="text-xl text-[#E6D5AC]">{h}</h2><p className="mt-3 text-[#F3EFE6]/75 leading-relaxed">{p}</p></section>)}
      </div>
    </div>
  );
}

export function Track() {
  const { t, lang } = useT();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ ref: sp.get("ref") || "", phone: "" });
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const check = async (e) => {
    e.preventDefault();
    setErr(""); setRes(null);
    try { setRes((await api.get("/public/track", { params: { ...f, lang } })).data); } catch (e2) { setErr(e2?.response?.data?.detail || "Erro"); }
  };
  return (
    <div className="max-w-[760px] mx-auto px-5 sm:px-10 pt-16" data-testid="track-page">
      <PageHead title={t.track_title} sub={t.track_sub} />
      <form onSubmit={check} className="mt-12 grid sm:grid-cols-[1fr_1fr_auto] gap-6 items-end">
        <input className="field" placeholder={`${t.track_ref} (GT-…)`} value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} data-testid="track-ref-input" />
        <input className="field" inputMode="numeric" maxLength={4} placeholder={t.track_phone} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="track-phone-input" />
        <button className="btn-gold" disabled={!f.ref || f.phone.length < 4} data-testid="track-submit">{t.track_btn}</button>
      </form>
      {err && <p className="mt-6 text-sm text-[#e0937f]" role="alert" data-testid="track-error">{err}</p>}
      {res && (
        <div className="mt-12 border border-[#C8AE6E]/25 bg-[#161512] p-6 sm:p-8 reveal" data-testid="track-result">
          <p className="eyebrow">{res.type} · {res.ref}</p>
          {res.watch_label && <p className="mt-3 font-display text-2xl text-[#F3EFE6]">{res.watch_label}</p>}
          {res.specific && <p className="mt-3 font-display text-2xl text-[#F3EFE6]">{[res.specific.brand, res.specific.model, res.specific.reference].filter(Boolean).join(" ")}</p>}
          {res.visit_date && <p className="mt-3 text-sm text-[#F3EFE6]" data-testid="track-visit">{t.track_visit}: {res.visit_date.split("-").reverse().join("/")} · {res.visit_time}</p>}
          <p className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{t.track_status}</p>
          <p className="mt-1 text-xl text-[#E6D5AC]" data-testid="track-status">{res.status_label}</p>
          <p className="mt-8 text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{t.track_history}</p>
          <ol className="mt-3 space-y-2 border-l border-[#C8AE6E]/30 pl-4">{res.history.map((h, i) => <li key={i} className="text-sm"><span className="text-[#F3EFE6]">{h.status_label}</span> <span className="text-[#A39E93] text-xs ml-2">{fmtDate(h.at)}</span></li>)}</ol>
        </div>
      )}
    </div>
  );
}
